/**
 * -----------------------------------------------------------------------------
 * Popup - Buzzer Alert Single
 * -----------------------------------------------------------------------------
 * This popup is used for Buzzer Alert Single
 */

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { TModalsState } from "../../api/store/commonSlice";
import { LegacyOrganizationAccount } from "../../api/types/Account";
import { BuzzerAlertStatuses } from "../../api/types/Notifications";
import svgIcons from "../../assets/svg/svgIcons";
import { AdminFormFieldSubmit } from "../../components/admin/formFields";
import FTModal from "../../components/common/FTModal";
import { APP_CONFIG } from "../../constants/constants";
import useBuzzerAlertActions from "../../hooks/buzzerAlertActions";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
} from "../../utils/dateTime";
import BuzzerAudio from "./buzzerAudio";
import { useAccountTimezone } from "../../hooks/timezone";

type TModalBuzzerAlertSingleProps = {};

const ModalBuzzerAlertSingle = (props: TModalBuzzerAlertSingleProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "buzzerAlert.single",
  });
  const { t: tBuzzerAlert } = useTranslation("translation", {
    keyPrefix: "buzzerAlert",
  });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const { tzForDateFns, tzForDisplay } = useAccountTimezone();

  const unreadBuzzerAlerts =
    modalsState?.buzzerAlerts
      ?.filter((alert) =>
        [BuzzerAlertStatuses.PENDING, BuzzerAlertStatuses.INPROGRESS].includes(
          alert.status
        )
      ) // only pending or in-progress alerts are considered unread
      ?.filter(
        (alert) =>
          !modalsState.buzzerAlertsViewed
            .map((alert) => alert.uuid)
            .includes(alert.uuid)
      ) // filter out viewed alerts
      ?.filter(
        (alert) =>
          !modalsState.buzzerAlertsInProgress
            .map((alert) => alert.uuid)
            .includes(alert.uuid)
      ) || []; // filter out alerts that are recently marked as in progress
  const thisBuzzerAlert = unreadBuzzerAlerts?.[0];

  const { markViewedInProgressAndNavigateToListing } = useBuzzerAlertActions();
  const viewAlertCallback = () => {
    markViewedInProgressAndNavigateToListing(unreadBuzzerAlerts);
  };

  if (modalsState.showBuzzerAlertSingle === false || !thisBuzzerAlert)
    return null;

  if (APP_CONFIG.DEBUG.BUZZER) console.error("POPUP AT", new Date());

  return (
    <FTModal
      containerStyle="max-w-[calc(100vw-4rem)] w-[560px]"
      dismissible={false}
      isBuzzerAlert={true}
      onHide={() => {}}
      position="center"
      visible={modalsState.showBuzzerAlertSingle}
    >
      <div className="relative flex flex-col items-center justify-center">
        {thisBuzzerAlert.status === BuzzerAlertStatuses.INPROGRESS && (
          <span className="absolute top-2 right-2 uppercase text-right text-[#E23131] bg-[#E2313133] font-normal text-xs rounded-sm px-2 leading-5">
            {tBuzzerAlert(`status_${thisBuzzerAlert.status}`)}
          </span>
        )}
        <img src={svgIcons.Hooter} />
        <h2 className="font-bold text-3xl uppercase mt-8 text-[#FF0E0E]">
          {t("heading")}
        </h2>
        <h4 className="font-bold text-lg uppercase mt-4 text-[#E23131]">
          {thisBuzzerAlert.rule_description}
        </h4>
        <div className="text-xs mt-3 text-heading-gray888">
          {!!tzForDateFns
            ? formattedDateTimeBasedOnTimezone(
                dbTimeToJsTime(parseInt(`${thisBuzzerAlert.timestamp}`)),
                tzForDateFns,
                "MM/dd/yyyy - hh:mmaa"
              )
            : new Date(
                parseInt(`${thisBuzzerAlert.timestamp}`)
              ).toLocaleString()}
          {!!tzForDisplay && ` (${tzForDisplay})`}
        </div>
        <div className="text-xs mt-3 text-heading-gray666 leading-5 text-center">
          <p className="font-bold">
            {t("asset")}: {thisBuzzerAlert.device_id}
          </p>
          <p>
            <span className="font-bold">{t("location")}:</span>{" "}
            {thisBuzzerAlert?.event_info?.address || "-"}
          </p>
          {thisBuzzerAlert.event_info?.latitude &&
            thisBuzzerAlert.event_info?.longitude && (
              <p>
                <span className="font-bold">{t("latlng")}:</span>{" "}
                <a
                  className="text-accent-blue-bright underline"
                  href={APP_CONFIG.LAT_LNG_LINK(
                    thisBuzzerAlert.event_info.latitude,
                    thisBuzzerAlert.event_info.longitude
                  )}
                  target="_blank"
                >{`${thisBuzzerAlert.event_info.latitude},${thisBuzzerAlert.event_info.longitude}`}</a>
              </p>
            )}
        </div>
        <AdminFormFieldSubmit
          label={t("button_view_alert")}
          onClick={viewAlertCallback}
          type="button"
          variant="primary"
          customWrapperClass="w-full mt-8 mb-2"
        />
        <BuzzerAudio />
      </div>
    </FTModal>
  );
};
export default ModalBuzzerAlertSingle;
